import { map, Observable, tap } from 'rxjs';
import { FilterSetup } from '@services/core-services/filters/filter-setup';
import { FilterEntry } from '@services/core-services/filters/filter-query';
import { UserSettingsService } from '@services/core-services/user-settings.service';
import { v4 as uuidv4 } from 'uuid';
import { FilterOptionsService } from '@services/core-services/filters/filter-options.service';
import { getFilterSetupFromOption } from '@services/core-services/filters/filter.helpers';

export interface FilterContext {
  rules?: FilterEntry[];
  name: string;
  id?: string;
}

export interface FilterProvider {
  getFilters?(): Observable<FilterContext[]>;

  saveFilter?(context: FilterContext): void;

  removeFilter?(context: FilterContext): void;

  getSetup(): Observable<FilterSetup>;
}

export abstract class FilterBaseProvider implements FilterProvider {
  protected filters: FilterContext[] = [];
  protected defaultFilters: FilterContext[] = [];

  protected constructor(
    protected readonly name: string,
    protected readonly userSettings: UserSettingsService,
    protected readonly filterOptionsService: FilterOptionsService
  ) {}

  abstract MODULE: string;

  private getCustomFilters() {
    return this.filters.filter(f => !!f.id);
  }

  getFilters(): Observable<FilterContext[]> {
    return this.userSettings.getSettingsEntry(this.name).pipe(
      map(filters => [...this.defaultFilters, ...(filters || [])]),
      tap(filters => {
        return (this.filters = filters);
      })
    );
  }

  saveFilter(context: FilterContext) {
    let filters;
    const customFilters = this.getCustomFilters();
    if (context.id) {
      filters = customFilters.map(f => (f.id === context.id ? context : f));
    } else {
      context.id = uuidv4();
      filters = customFilters.concat([context]);
    }
    this.userSettings.saveSettingsEntry(this.name, filters);
  }

  removeFilter(context: FilterContext) {
    const filters = this.getCustomFilters().filter(f => f.id !== context.id);
    this.userSettings.saveSettingsEntry(this.name, filters);
  }

  getSetup(): Observable<FilterSetup> {
    return this.filterOptionsService
      .getFilterOptions(this.MODULE)
      .pipe(map(option => getFilterSetupFromOption(option)));
  }
}
